import { getActions } from '../global';

import type { ParsedSpaceChatDeepLink } from './spaceChatDeepLinkParser';

import { toChannelId } from '../global/helpers';
import { tryParseSpaceChatDeepLink } from './spaceChatDeepLinkParser';

/**
 * Process a SpaceChat deep link and navigate to the appropriate screen
 *
 * @param url - The deep link URL (sc:// or spacechat://)
 * @returns true if the link was successfully processed, false otherwise
 */
export function processSpaceChatDeepLink(url: string): boolean {
  const parsedLink = tryParseSpaceChatDeepLink(url);

  if (!parsedLink) {
    return false;
  }

  return handleParsedDeepLink(parsedLink);
}

/**
 * Handle a parsed deep link by dispatching the appropriate action
 */
function handleParsedDeepLink(link: ParsedSpaceChatDeepLink): boolean {
  const actions = getActions();

  switch (link.type) {
    case 'chatById': {
      actions.openChat({ id: link.chatId });
      return true;
    }

    case 'chatByUsername': {
      actions.openChatByUsername({ username: link.username });
      return true;
    }

    case 'userById': {
      // For users, we open a chat with them (private chat)
      actions.openChat({ id: link.userId });
      return true;
    }

    case 'userByUsername': {
      actions.openChatByUsername({ username: link.username });
      return true;
    }

    case 'channel': {
      // Channels need toChannelId conversion for internal ID format
      // The internal format uses -1 prefix for channels
      actions.openChat({ id: toChannelId(link.channelId) });
      return true;
    }

    case 'group': {
      // Groups use the ID directly (negative number format)
      actions.openChat({ id: link.groupId });
      return true;
    }

    case 'invite': {
      actions.openChatByInvite({ hash: link.hash });
      return true;
    }

    case 'message': {
      // For message links, we need to determine the correct chat ID format
      // If chatId is numeric and doesn't start with -, it might be a channel
      const chatId = isChannelId(link.chatId)
        ? toChannelId(link.chatId)
        : link.chatId;

      // In forum channels, the URL format is often /c/{channelId}/{topicId}
      // where topicId is a thread ID, not a message ID
      // If we have threadId explicitly, use focusMessage with both
      // Otherwise, the "messageId" is likely a topic ID for forum navigation
      if (link.threadId) {
        // We have both thread and message IDs - focus on specific message in thread
        actions.focusMessage({
          chatId,
          threadId: link.threadId,
          messageId: link.messageId,
        });
      } else {
        // The "messageId" is actually a topic/thread ID for forum channels
        // Use openThread to navigate to the topic
        actions.openThread({
          chatId,
          threadId: link.messageId,
          shouldReplaceHistory: true,
        });
      }
      return true;
    }

    default:
      return false;
  }
}

/**
 * Check if an ID looks like a channel ID (positive number without prefix)
 * Channels in MTProto have positive IDs, but internally we use -1{id} format
 */
function isChannelId(id: string): boolean {
  // If the ID is a positive number (doesn't start with -), it's likely a channel
  return /^\d+$/.test(id) && !id.startsWith('-');
}
